"use client";

import { useCallback, useMemo } from "react";

import {
  profileExportAdapter,
  profileExportLabel,
  useExportBusy,
  useStartExportJob,
} from "@/features/exports/tracker";
import { usePipelineJob } from "@/features/jobs/context/pipeline-job-context";
import type { ExportOptions } from "@/features/search/components/actions/export";
import {
  requestExport,
  type ExportFormat,
  type ProfileExportSource,
} from "@/hooks/use-search";
import type { PipelineCandidate } from "@/lib/api/types";

/**
 * Export tracker scopes. The row menu and the profile sheet share one scope
 * per profile, so a profile can't be exported twice at once.
 */
export const pipelineExportScope = {
  all: (jobId: string) => `pipeline:${jobId}:all`,
  bulk: (jobId: string) => `pipeline:${jobId}:bulk`,
  profile: (jobId: string, profileId: string) =>
    `pipeline:${jobId}:profile:${profileId}`,
};

/** Export one candidate; the tracker downloads the file when it's ready. */
export function usePipelineProfileExport(candidate: PipelineCandidate | null) {
  const { job } = usePipelineJob();
  const startExportJob = useStartExportJob();

  const scope = candidate
    ? pipelineExportScope.profile(job.id, candidate.profile_id)
    : null;
  const exporting = useExportBusy(scope);

  const source = useMemo<ProfileExportSource | null>(
    () =>
      candidate
        ? {
            type: "pipeline",
            job_id: job.id,
            job_candidate_ids: [candidate.job_candidate_id],
          }
        : null,
    [candidate, job.id],
  );

  const startExport = useCallback(
    (
      format: ExportFormat,
      includeContacts: boolean,
      options: ExportOptions,
    ) => {
      if (!source || !scope) return;
      void startExportJob({
        scope,
        label: profileExportLabel(1, format),
        format,
        adapter: profileExportAdapter,
        post: () =>
          requestExport({
            source,
            format,
            include_contacts: includeContacts,
            options,
          }),
      });
    },
    [scope, source, startExportJob],
  );

  return { source, exporting, startExport };
}
